import React from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, XMarkIcon, ArrowUpTrayIcon, MagnifyingGlassIcon, UserPlusIcon, RocketLaunchIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLeadsPage } from './useLeadsPage';
import LeadsTable from './LeadsTable';
import LeadsMobileCards from './LeadsMobileCards';
import LeadsPreviewModal from './LeadsPreviewModal';
import AddLeadModal from './AddLeadModal';
import CallLeadModal from './CallLeadModal';
import RunCampaignModal from './RunCampaignModal';
import DeleteLeadConfirmModal from './DeleteLeadConfirmModal';
import DeleteLeadsBulkConfirmModal from './DeleteLeadsBulkConfirmModal';
import type { Lead } from './leadsTypes';
import MultiSelectFilter from '../../components/filters/MultiSelectFilter';
import TimeRangeFilter from '../../components/filters/TimeRangeFilter';
import SendSMSModal, { type SmsLeadTarget } from '../../components/SendSMSModal';
import SendEmailModal, { type EmailLeadTarget } from '../../components/SendEmailModal';
import BulkSMSModal from '../../components/BulkSMSModal';
import BulkEmailModal from '../../components/BulkEmailModal';
import { ChatBubbleLeftRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useSmsEligibility } from '../../utils/entitlements';

const LeadsPage: React.FC = () => {
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);
  // LT-92: email gates at the same Growth+ tier as SMS (backend check is the source of truth;
  // this just hides the buttons for ineligible plans).
  const canEmail = canSms;
  const {
    leads,
    runs,
    loading,
    uploading,
    toast,
    setToast,
    showPreviewModal,
    previewData,
    fileInputRef,
    searchTerm,
    setSearchTerm,
    filterRunIds,
    setFilterRunIds,
    filterTemperatures,
    setFilterTemperatures,
    filterDispositions,
    setFilterDispositions,
    filterSources,
    setFilterSources,
    timeRange,
    setTimeRange,
    runOptions,
    dispositionOptions,
    sourceOptions,
    leadTemperatureOptions,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    requestSort,
    editingId,
    editData,
    setEditData,
    currentPage,
    setCurrentPage,
    totalPages,
    totalLeads,
    itemsPerPage,
    formatDateDDMMYYYY,
    getRunName,
    handleEdit,
    handleSave,
    handleCancel,
    handleFileSelect,
    handleUploadClick,
    handleConfirmUpload,
    handleCancelPreview,
    showAddLeadModal,
    addLeadForm,
    setAddLeadForm,
    addLeadSubmitting,
    handleOpenAddLead,
    handleCloseAddLead,
    handleAddLeadSubmit,
    selectedLeadIds,
    selectedLeads,
    isAllOnPageSelected,
    toggleSelection,
    selectAllOnPage,
    clearSelection,
    handleRunCampaign,
    handleRunCampaignForLead,
    leadToCall,
    handleCloseCallModal,
    showRunCampaignModal,
    handleCloseRunCampaignModal,
    leadToDelete,
    openDeleteConfirm,
    closeDeleteConfirm,
    handleConfirmDeleteLead,
    deletingLead,
    showBulkDeleteConfirm,
    openBulkDeleteConfirm,
    closeBulkDeleteConfirm,
    handleConfirmBulkDelete,
    deletingBulk,
  } = useLeadsPage();

  const [smsLead, setSmsLead] = React.useState<SmsLeadTarget | null>(null);
  const [emailLead, setEmailLead] = React.useState<EmailLeadTarget | null>(null);
  const [showBulkSms, setShowBulkSms] = React.useState(false);
  const [showBulkEmail, setShowBulkEmail] = React.useState(false);

  const setHistoryLead = (_lead: Lead | null) => {};

  const toastEl = toast ? (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-full mx-4 pointer-events-auto">
      <div
        className={`p-3 rounded-lg flex items-center justify-between shadow-lg ${
          toast.type === 'success' ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'
        }`}
      >
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          {toast.type === 'success' ? (
            <CheckIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
          ) : (
            <XMarkIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
          )}
          <span className={`text-sm whitespace-pre-line ${toast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{toast.message}</span>
        </div>
        <button type="button" onClick={() => setToast(null)} className="text-gray-400 hover:text-white transition-colors flex-shrink-0 ml-2">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="relative min-h-screen">
      {toastEl && createPortal(toastEl, document.body)}

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
        <div className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
      </div>

      <div className="relative mx-auto px-2 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-slate-50">
                Leads Management
              </h1>
              <p className="text-gray-300 text-sm sm:text-base lg:text-lg mt-1">Manage all your leads across campaigns</p>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleOpenAddLead}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg text-sm"
              >
                <UserPlusIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Add Lead</span>
                <span className="sm:hidden">Add</span>
              </button>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={uploading}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Upload CSV/Excel</span>
                <span className="sm:hidden">Upload</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
            <div className="sm:w-72">
              <MultiSelectFilter
                label="Campaign"
                options={runOptions}
                selectedValues={filterRunIds}
                onChange={setFilterRunIds}
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
              <MultiSelectFilter
                label="Lead Temp"
                options={leadTemperatureOptions}
                selectedValues={filterTemperatures}
                onChange={setFilterTemperatures}
              />
              <MultiSelectFilter
                label="Disposition"
                options={dispositionOptions}
                selectedValues={filterDispositions}
                onChange={setFilterDispositions}
              />
              <MultiSelectFilter
                label="Source"
                options={sourceOptions}
                selectedValues={filterSources}
                onChange={setFilterSources}
              />
            </div>
            <select
              value={`${sortField}:${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split(':');
                setSortField(field as typeof sortField);
                setSortDirection(direction as typeof sortDirection);
              }}
              className="text-xs bg-slate-700/50 text-white border border-slate-600 rounded px-2 py-1.5 sm:ml-auto"
            >
              <option value="created_at:desc">Newest first</option>
              <option value="created_at:asc">Oldest first</option>
              <option value="name:asc">Name A-Z</option>
              <option value="name:desc">Name Z-A</option>
              <option value="current_temperature:asc">Lead Temp (Hot to Dead)</option>
              <option value="current_temperature:desc">Lead Temp (Dead to Hot)</option>
              <option value="current_disposition:asc">Disposition A-Z</option>
              <option value="attempts:desc">Most attempts</option>
              <option value="attempts:asc">Fewest attempts</option>
              <option value="last_contact_at:desc">Recent contact</option>
              <option value="last_contact_at:asc">Oldest contact</option>
            </select>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/50 backdrop-blur-xl shadow-2xl rounded-xl border border-slate-700/40 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No leads found</div>
          ) : (
            <>
              {selectedLeads.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-3 xl:px-4 py-3 bg-cyan-500/10 border-b border-cyan-500/20">
                  <span className="text-sm text-cyan-200">
                    <strong>{selectedLeads.length}</strong> lead{selectedLeads.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="px-3 py-1.5 text-xs sm:text-sm text-gray-300 hover:text-white border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      Clear selection
                    </button>
                    {canSms && (
                      <button
                        type="button"
                        onClick={() => setShowBulkSms(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all shadow-lg text-sm font-medium"
                      >
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                        Send SMS
                      </button>
                    )}
                    {canEmail && (
                      <button
                        type="button"
                        onClick={() => setShowBulkEmail(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all shadow-lg text-sm font-medium"
                      >
                        <EnvelopeIcon className="w-4 h-4" />
                        Send Email
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleRunCampaign}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg text-sm font-medium"
                    >
                      <RocketLaunchIcon className="w-4 h-4" />
                      Run Campaign
                    </button>
                    <button
                      type="button"
                      onClick={openBulkDeleteConfirm}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all shadow-lg text-sm font-medium border border-red-500/30"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete Leads
                    </button>
                  </div>
                </div>
              )}
              <LeadsTable
                leads={leads}
                runs={runs}
                editingId={editingId}
                editData={editData}
                setEditData={setEditData}
                getRunName={getRunName}
                formatDateDDMMYYYY={formatDateDDMMYYYY}
                onEdit={handleEdit}
                onSave={handleSave}
                onCancel={handleCancel}
                onRunCampaignForLead={handleRunCampaignForLead}
                onOpenDeleteConfirm={openDeleteConfirm}
                onSendSms={canSms ? (lead) => setSmsLead({ id: lead.id, name: lead.name, phone: lead.phone }) : undefined}
                onSendEmail={(lead) => {
                  const em = (lead.email || '').trim();
                  if (em) setEmailLead({ id: lead.id, name: lead.name, email: em });
                }}
                onViewHistory={setHistoryLead}
                selectedLeadIds={selectedLeadIds}
                isAllOnPageSelected={isAllOnPageSelected}
                onToggleSelection={toggleSelection}
                onSelectAllOnPage={selectAllOnPage}
                sortField={sortField}
                sortDirection={sortDirection}
                onRequestSort={requestSort}
              />
              <LeadsMobileCards
                leads={leads}
                runs={runs}
                editingId={editingId}
                editData={editData}
                setEditData={setEditData}
                getRunName={getRunName}
                formatDateDDMMYYYY={formatDateDDMMYYYY}
                onEdit={handleEdit}
                onSave={handleSave}
                onCancel={handleCancel}
                onRunCampaignForLead={handleRunCampaignForLead}
                onOpenDeleteConfirm={openDeleteConfirm}
                onSendSms={canSms ? (lead) => setSmsLead({ id: lead.id, name: lead.name, phone: lead.phone }) : undefined}
                onSendEmail={(lead) => {
                  const em = (lead.email || '').trim();
                  if (em) setEmailLead({ id: lead.id, name: lead.name, email: em });
                }}
                canSendEmail={canEmail}
                onViewHistory={setHistoryLead}
                selectedLeadIds={selectedLeadIds}
                isAllOnPageSelected={isAllOnPageSelected}
                onToggleSelection={toggleSelection}
                onSelectAllOnPage={selectAllOnPage}
              />

              {totalLeads > itemsPerPage && (
                <div className="mt-3 pt-3 pb-4 mb-1 border-t border-slate-700/30">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-4">
                    <div className="text-xs text-gray-300 text-center">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalLeads)} of {totalLeads} leads
                    </div>
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-2 py-1 text-xs bg-white/10 text-white rounded-md hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Prev
                      </button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 3) pageNum = i + 1;
                          else if (currentPage <= 2) pageNum = i + 1;
                          else if (currentPage >= totalPages - 1) pageNum = totalPages - 2 + i;
                          else pageNum = currentPage - 1 + i;
                          return (
                            <button
                              key={pageNum}
                              type="button"
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                currentPage === pageNum ? 'bg-cyan-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-2 py-1 text-xs bg-white/10 text-white rounded-md hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <AddLeadModal
          isOpen={showAddLeadModal}
          onClose={handleCloseAddLead}
          form={addLeadForm}
          setForm={setAddLeadForm}
          onSubmit={handleAddLeadSubmit}
          submitting={addLeadSubmitting}
          runs={runs}
        />
        <CallLeadModal
          isOpen={!!leadToCall}
          onClose={handleCloseCallModal}
          lead={leadToCall}
          setToast={setToast}
        />
        <RunCampaignModal
          isOpen={showRunCampaignModal}
          onClose={handleCloseRunCampaignModal}
          selectedLeads={selectedLeads}
          setToast={setToast}
        />
        {showPreviewModal && previewData && (
          <LeadsPreviewModal
            previewData={previewData}
            filterRunId={filterRunIds.length === 1 ? filterRunIds[0] : ''}
            runName={filterRunIds.length === 1 ? runs.get(filterRunIds[0])?.name : undefined}
            uploading={uploading}
            onCancel={handleCancelPreview}
            onConfirm={handleConfirmUpload}
          />
        )}
        <DeleteLeadConfirmModal
          isOpen={!!leadToDelete}
          lead={leadToDelete}
          onClose={closeDeleteConfirm}
          onConfirm={handleConfirmDeleteLead}
          deleting={deletingLead}
        />
        <DeleteLeadsBulkConfirmModal
          isOpen={showBulkDeleteConfirm}
          count={selectedLeads.length}
          onClose={closeBulkDeleteConfirm}
          onConfirm={handleConfirmBulkDelete}
          deleting={deletingBulk}
        />
        <SendSMSModal
          isOpen={!!smsLead}
          onClose={() => setSmsLead(null)}
          lead={smsLead}
          setToast={setToast}
        />
        <SendEmailModal
          isOpen={!!emailLead}
          onClose={() => setEmailLead(null)}
          lead={emailLead}
          setToast={setToast}
        />
        <BulkSMSModal
          isOpen={showBulkSms}
          onClose={() => setShowBulkSms(false)}
          leads={selectedLeads.map((l) => ({ id: l.id, name: l.name, phone: l.phone }))}
          setToast={setToast}
        />
        <BulkEmailModal
          isOpen={showBulkEmail}
          onClose={() => setShowBulkEmail(false)}
          leads={selectedLeads.map((l) => ({ id: l.id, name: l.name, email: l.email || '' }))}
          setToast={setToast}
        />
      </div>

    </div>
  );
};

export default LeadsPage;
